import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import supabase from "../supabase";
import { listProjectsForUser } from "../lib/projects";
import { getDisplayStatus } from "../lib/articles";

// ----- Helpers -----
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const initials = (nameOrEmail = "?") =>
  nameOrEmail
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("") || "?";

// ----- Donut Chart Component -----
const DonutBreakdown = ({ breakdown, size = 120, stroke = 16 }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const total = breakdown.reduce((sum, b) => sum + (b.value || 0), 0);

  if (!total) {
    return (
      <svg width={size} height={size} style={{ display: "block" }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="#e5e7eb"
          strokeWidth={stroke}
          fill="none"
        />
      </svg>
    );
  }

  let offset = 0;
  return (
    <svg width={size} height={size} style={{ display: "block" }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke="#e5e7eb"
        strokeWidth={stroke}
        fill="none"
      />
      {breakdown.map((b) => {
        const pct = (b.value / total) * 100;
        const dash = (pct / 100) * c;
        const segment = (
          <circle
            key={b.label}
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke={b.color}
            strokeWidth={stroke}
            fill="none"
            strokeLinecap="butt"
            strokeDasharray={`${dash} ${c - dash}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        );
        offset += dash;
        return segment;
      })}
    </svg>
  );
};

export default function Dashboard({ profile, organization }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // Data states
  const [projects, setProjects] = useState([]);
  const [projectArticleCounts, setProjectArticleCounts] = useState({});
  const [articles, setArticles] = useState([]);
  const [members, setMembers] = useState([]);
  const [orgName, setOrgName] = useState("");

  // Stats states
  const [metrics, setMetrics] = useState({
    total: 0,
    inProgress: 0,
    pending: 0,
    completed: 0,
  });
  const [healthBreakdown, setHealthBreakdown] = useState([]);

  // ✅ Function to handle opening an article in the editor
  const handleEditArticle = async (partialArticle) => {
    try {
      // 1. Fetch the FULL article (including content_html, meta_tags, brief)
      const { data: fullArticle, error } = await supabase
        .from("articles")
        .select(
          `
          id,
          title,
          content_html,
          meta_tags,
          content_brief, 
          progress,
          score,
          readability_score,
          topic_authority_score,
          content_value_score,
          seo_score,
          created_by,
          project_id,
          created_at,
          updated_at,
          Users:created_by (
            id,
            name,
            email
          )
        `
        )
        .eq("id", partialArticle.id)
        .single();

      if (error) throw error;

      // 2. Navigate to the editor with the full object
      navigate(
        `/organization-portal/${profile.company_id}/content-generation`,
        {
          state: { openArticle: fullArticle },
        }
      );
    } catch (err) {
      console.error("Error opening article:", err);
      alert("Failed to open article.");
    }
  };

  useEffect(() => {
    if (!profile) return;

    const loadDashboardData = async () => {
      setLoading(true);
      try {
        // 1. Fetch Organization Name
        if (profile.company_id) {
          const { data: orgData, error: orgErr } = await supabase
            .from("Organizations")
            .select("name")
            .eq("id", profile.company_id)
            .single();

          if (!orgErr && orgData) {
            setOrgName(orgData.name);
          }
        }

        // 2. Fetch Members (Including Avatar URL)
        let memberData = [];
        if (profile.company_id) {
          const { data: memData, error: memError } = await supabase
            .from("Users")
            .select("id, name, email, role, avatar_url")
            .eq("company_id", profile.company_id)
            .order("name", { ascending: true });
          if (!memError) memberData = memData || [];
          setMembers(memberData);
        }

        // 3. Fetch Projects
        const myProjects = await listProjectsForUser({
          user_id: profile.id,
          role: profile.role,
          company_id: profile.company_id, // ✅ ADDED SECURITY
        });

        const projectIds = myProjects.map((p) => p.id);
        let projData = [];
        let counts = {};

        if (projectIds.length > 0) {
          const { data, error } = await supabase
            .from("projects")
            .select(
              "id, title, description, organization, writers, editors, updated_at, created_at, created_by"
            )
            .in("id", projectIds)
            .order("updated_at", { ascending: false })
            .limit(5);
          if (!error) projData = data;

          const { data: artCountData, error: artCountErr } = await supabase
            .from("articles")
            .select("id, project_id")
            .in("project_id", projectIds);

          if (!artCountErr && artCountData) {
            artCountData.forEach((a) => {
              if (a.project_id) {
                counts[a.project_id] = (counts[a.project_id] || 0) + 1;
              }
            });
          }
        }
        setProjects(projData || []);
        setProjectArticleCounts(counts);

        // 4. Fetch Recent Articles
        let articleQuery = supabase
          .from("articles")
          .select(
            `
            id, title, progress, score, updated_at, project_id, 
            Users:created_by(name, email),
            projects(title)
          `
          )
          .eq("org_id", profile.company_id) // ✅ ADDED SECURITY
          .order("updated_at", { ascending: false })
          .limit(10);

        if ((profile.role || "").toLowerCase() !== "admin") {
          articleQuery = articleQuery.eq("created_by", profile.id);
        }

        const { data: artData, error: artError } = await articleQuery;
        if (!artError) setArticles(artData || []);

        // 5. Fetch Stats
        let statsQuery = supabase
          .from("articles")
          .select("progress")
          .eq("org_id", profile.company_id); // ✅ ADDED SECURITY

        if ((profile.role || "").toLowerCase() !== "admin") {
          statsQuery = statsQuery.eq("created_by", profile.id);
        }

        const { data: allStats, error: statsError } = await statsQuery;

        if (!statsError && allStats) {
          let total = allStats.length;
          let inProgress = 0;
          let pending = 0;
          let completed = 0;

          const bucketCounts = {
            "In Progress": 0,
            "Ready for Review": 0,
            "Edits Needed": 0,
            "Ready for Approval": 0,
            "Ready to Publish": 0,
          };

          allStats.forEach((a) => {
            const rawP = getDisplayStatus(a.progress, organization?.ai_enabled);
            const p = rawP.toLowerCase().trim();
            const isCompleted = p === "ready to publish" || p === "completed";
            const isInProgress = p === "in progress";

            if (isCompleted) completed++;
            if (!isInProgress && !isCompleted) pending++;
            if (!isCompleted) inProgress++;

            let bucket = "In Progress";
            if (p.includes("review")) bucket = "Ready for Review";
            else if (p.includes("edit")) bucket = "Edits Needed";
            else if (p.includes("approval")) bucket = "Ready for Approval";
            else if (p.includes("publish") || p.includes("complete"))
              bucket = "Ready to Publish";

            bucketCounts[bucket] = (bucketCounts[bucket] || 0) + 1;
          });

          setMetrics({ total, inProgress, pending, completed });

          const bucketsConfig = organization?.ai_enabled
            ? [
                { label: "In Progress", color: "#98fb98" },
                { label: "Ready for Review", color: "#48a860" },
                { label: "Edits Needed", color: "#2e8b57" },
                { label: "Ready for Approval", color: "#3f704d" },
                { label: "Ready to Publish", color: "#1a2421" },
              ]
            : [
                { label: "In Progress", color: "#98fb98" },
                { label: "Ready for Approval", color: "#3f704d" },
                { label: "Ready to Publish", color: "#1a2421" },
              ];

          setHealthBreakdown(
            bucketsConfig.map((b) => ({
              ...b,
              value: bucketCounts[b.label] || 0,
            }))
          );
        }
      } catch (err) {
        console.error("Dashboard load error:", err);
      } finally {
        setLoading(false);
      }
    };
    loadDashboardData();
  }, [profile, organization]);

  const getStatusStyle = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("publish")) return styles.badgePublished;
    if (s.includes("review")) return styles.badgeReview;
    if (s.includes("draft") || s.includes("progress"))
      return styles.badgeInProgress;
    return styles.badgeDefault;
  };

  const recentActivity = useMemo(() => {
    return articles.slice(0, 5).map((a) => ({
      id: a.id,
      title: a.title || "Untitled",
      status: getDisplayStatus(a.progress, organization?.ai_enabled),
      user: a.Users?.name || "Someone",
      time: new Date(a.updated_at).toLocaleDateString(),
    }));
  }, [articles, organization]);

  const renderPeople = (ids) => {
    const list = (ids || []).filter(Boolean);
    const shown = list.slice(0, 3);
    const extra = Math.max(0, list.length - shown.length);

    return (
      <div style={styles.avatarStack}>
        {shown.map((id) => {
          const u = members.find((m) => m.id === id);
          const label = u?.name || u?.email || "?";
          return (
            <div key={id} title={label} style={styles.avatar}>
              {/* Check if user has avatar_url, otherwise initials */}
              {u?.avatar_url ? (
                <img
                  src={u.avatar_url}
                  alt={label}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                />
              ) : (
                initials(label)
              )}
            </div>
          );
        })}
        {extra > 0 && <div style={styles.avatarMore}>+{extra}</div>}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.headerCard}>Loading Dashboard...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* 1. Header */}
      <div style={styles.headerCard}>
        <div style={styles.headerTop}>
          <h1 style={styles.h1}>
            Welcome back, {profile?.name?.split(" ")[0] || "User"}!
          </h1>
          {orgName && <div style={styles.orgName}>{orgName}</div>}
        </div>
        <p style={styles.description}>
          Here is what’s happening in your organization today.
        </p>
      </div>

      {/* 2. Top Row: Stats */}
      <div style={styles.statsRow}>
        <div style={styles.statBox}>
          <div style={styles.statLabel}>Total Articles</div>
          <div style={styles.statValue}>{metrics.total}</div>
        </div>
        <div style={styles.statBox}>
          <div style={styles.statLabel}>In Progress</div>
          <div style={styles.statValue}>{metrics.inProgress}</div>
          <div style={styles.statSub}>Active drafts & edits</div>
        </div>
        <div style={styles.statBox}>
          <div style={styles.statLabel}>Pending Reviews</div>
          <div style={styles.statValue}>{metrics.pending}</div>
          <div style={styles.statSub}>Needs review or approval</div>
        </div>
        <div style={styles.statBox}>
          <div style={styles.statLabel}>Completed</div>
          <div style={styles.statValue}>{metrics.completed}</div>
          <div style={styles.statSub}>Ready to publish</div>
        </div>
      </div>

      {/* 3. Middle Row: 3 Equal Columns */}
      <div style={styles.middleGrid}>
        {/* Left: Recent Activity */}
        <div style={styles.visualCard}>
          <div style={styles.cardHeader}>
            <div style={styles.sectionTitle}>Recent Activity</div>
          </div>
          <div style={styles.activityList}>
            {recentActivity.map((act) => (
              <div key={act.id} style={styles.activityRow}>
                <div style={styles.activityIcon}>📝</div>
                <div>
                  <div style={styles.activityText}>
                    <strong>{act.title}</strong> is now{" "}
                    <span style={{ color: "#00876e", fontWeight: 600 }}>
                      {act.status}
                    </span>
                    .
                  </div>
                  <div style={styles.activityMeta}>
                    Updated by {act.user} on {act.time}
                  </div>
                </div>
              </div>
            ))}
            {recentActivity.length === 0 && (
              <div style={styles.emptyState}>No recent activity.</div>
            )}
          </div>
        </div>

        {/* Center: Content Health */}
        <div style={styles.visualCard}>
          <div style={styles.cardHeader}>
            <div style={styles.sectionTitle}>Content Health</div>
          </div>
          <div style={styles.donutContainer}>
            <DonutBreakdown
              breakdown={healthBreakdown}
              size={140}
              stroke={24}
            />
            <div style={styles.legend}>
              {healthBreakdown.map((b) => (
                <div key={b.label} style={styles.legendItem}>
                  <span
                    style={{ ...styles.legendDot, background: b.color }}
                  ></span>
                  <span style={styles.legendText}>
                    {b.label} ({b.value})
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Team Members */}
        <div style={styles.visualCard}>
          <div style={styles.cardHeader}>
            <div style={styles.sectionTitle}>Team Members</div>
          </div>
          <div style={styles.teamListContainer}>
            <div style={styles.teamList}>
              {members.map((m) => (
                <div key={m.id} style={styles.teamRow}>
                  {/* ✅ Avatar Update */}
                  <div style={{ ...styles.teamAvatarBig, overflow: "hidden" }}>
                    {m.avatar_url ? (
                      <img
                        src={m.avatar_url}
                        alt={m.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : m.name ? (
                      m.name[0].toUpperCase()
                    ) : (
                      "U"
                    )}
                  </div>
                  <div style={{ overflow: "hidden" }}>
                    <div style={styles.teamName}>{m.name || m.email}</div>
                    <div style={styles.teamRole}>{m.role || "Member"}</div>
                  </div>
                </div>
              ))}
              {members.length === 0 && (
                <div style={styles.emptyState}>No members found.</div>
              )}
            </div>
          </div>
          <button
            style={{ ...styles.settingsButton, marginTop: 12 }}
            onClick={() =>
              navigate(`/organization-portal/${profile.company_id}/members`)
            }
          >
            Manage Team
          </button>
        </div>
      </div>

      {/* 4. Bottom Row: Tables (Full Width) */}
      <div style={styles.bottomGrid}>
        {/* Projects Table */}
        <div style={styles.sectionCard}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>Recent Projects</div>
            <button
              style={styles.viewAllBtn}
              onClick={() =>
                navigate(`/organization-portal/${profile.company_id}/projects`)
              }
            >
              View All
            </button>
          </div>
          {projects.length === 0 ? (
            <div style={styles.emptyState}>No projects found.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Project Name</th>
                  <th style={styles.th}>Organization</th>
                  <th style={styles.th}>Writers</th>
                  <th style={styles.th}>Editors</th>
                  <th style={styles.th}>Article Count</th>
                  <th style={styles.th}>Last Updated</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id} style={styles.tr}>
                    <td style={styles.tdStrong}>{p.title}</td>
                    <td style={styles.td}>{p.organization}</td>
                    <td style={styles.td}>{renderPeople(p.writers)}</td>
                    <td style={styles.td}>{renderPeople(p.editors)}</td>
                    <td style={styles.td}>{projectArticleCounts[p.id] || 0}</td>
                    <td style={styles.td}>{fmtDate(p.updated_at)}</td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      <button
                        style={styles.smallBtn}
                        onClick={() =>
                          navigate(
                            `/organization-portal/${profile.company_id}/projects/${p.id}`
                          )
                        }
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Articles Table */}
        <div style={styles.sectionCard}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>Recent Articles</div>
            <button
              style={styles.viewAllBtn}
              onClick={() =>
                navigate(
                  `/organization-portal/${profile.company_id}/my-content`
                )
              }
            >
              View All
            </button>
          </div>
          {articles.length === 0 ? (
            <div style={styles.emptyState}>No articles found.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Project</th>
                  <th style={styles.th}>Writer</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Score</th>
                  <th style={styles.th}>Last Updated</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.id} style={styles.tr}>
                    <td style={styles.tdStrong}>{a.title || "Untitled"}</td>
                    <td style={styles.td}>
                      {a.projects?.title || "Unassigned"}
                    </td>
                    <td style={styles.td}>
                      {a.Users?.name || a.Users?.email || "—"}
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.badgeBase,
                          ...getStatusStyle(
                            getDisplayStatus(a.progress, organization?.ai_enabled)
                          ),
                        }}
                      >
                        {getDisplayStatus(a.progress, organization?.ai_enabled)}
                      </span>
                    </td>
                    <td style={styles.td}>{a.score || "-"}</td>
                    <td style={styles.td}>{fmtDate(a.updated_at)}</td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      <button
                        style={styles.smallBtn}
                        onClick={() => handleEditArticle(a)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: "28px",
    background: "#f6f7fb",
    minHeight: "100vh",
    color: "#111827",
  },
  headerCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 6px rgba(0,0,0,0.02)",
    padding: "24px",
    marginBottom: "24px",
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  h1: { margin: 0, fontSize: 24, fontWeight: 800 },
  orgName: {
    fontSize: 16,
    fontWeight: 600,
    color: "#6b7280",
  },
  description: { margin: 0, color: "#6b7280", fontSize: 14 },

  // Stats Row
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "24px",
    marginBottom: "24px",
  },
  statBox: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 6px rgba(0,0,0,0.02)",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
  },
  statLabel: {
    fontSize: 13,
    color: "#6b7280",
    fontWeight: 700,
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statValue: {
    fontSize: 28,
    fontWeight: 800,
    color: "#111827",
    lineHeight: 1,
  },
  statSub: {
    fontSize: 12,
    color: "#9ca3af",
    marginTop: 6,
  },

  // Middle Grid (3 Columns now)
  middleGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr", // 3 Equal Columns
    gap: "24px",
    marginBottom: "24px",
  },
  visualCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 6px rgba(0,0,0,0.02)",
    padding: "20px",
    minHeight: "220px",
    display: "flex",
    flexDirection: "column",
  },
  cardHeader: {
    marginBottom: "16px",
    borderBottom: "1px solid #f3f4f6",
    paddingBottom: "10px",
  },

  // Donut & Legend
  donutContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: "10px",
    gap: "20px",
  },
  legend: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    alignItems: "center",
  },
  legendItem: { display: "flex", alignItems: "center", gap: "8px" },
  legendDot: { width: 10, height: 10, borderRadius: "50%" },
  legendText: { fontSize: 13, color: "#374151", fontWeight: 600 },

  activityList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  activityRow: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    paddingBottom: "10px",
    borderBottom: "1px solid #f9fafb",
  },
  activityIcon: {
    background: "#e0f0ee",
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
  },
  activityText: { fontSize: 13, color: "#111827" },
  activityMeta: { fontSize: 11, color: "#9ca3af", marginTop: "2px" },

  // Bottom Grid (Full Width Stack)
  bottomGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },

  sectionCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 6px rgba(0,0,0,0.02)",
    padding: "20px",
    overflow: "hidden",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  sectionTitle: { fontSize: 16, fontWeight: 800, color: "#111827" },
  viewAllBtn: {
    background: "none",
    border: "none",
    color: "#00876e",
    fontWeight: 600,
    cursor: "pointer",
    fontSize: 13,
  },

  // Table Styles
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 700,
    color: "#9ca3af",
    padding: "10px 0",
    borderBottom: "1px solid #f3f4f6",
    textTransform: "uppercase",
    letterSpacing: "0.02em",
  },
  tr: { borderBottom: "1px solid #f9fafb" },
  td: { padding: "14px 0", fontSize: 14, verticalAlign: "middle" },
  tdStrong: {
    padding: "14px 0",
    fontSize: 14,
    fontWeight: 600,
    color: "#1f2937",
    verticalAlign: "middle",
  },
  emptyState: {
    color: "#6b7280",
    fontSize: 14,
    fontStyle: "italic",
    padding: "10px 0",
  },

  // Buttons
  smallBtn: {
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid #e5e7eb",
    background: "#fff",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    color: "#374151",
  },

  // Badges
  badgeBase: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  badgePublished: { background: "rgba(16,185,129,0.12)", color: "#065f46" },
  badgeInProgress: { background: "rgba(34,211,238,0.14)", color: "#0e7490" },
  badgeReview: { background: "rgba(59,130,246,0.12)", color: "#1d4ed8" },
  badgeDefault: { background: "#f3f4f6", color: "#374151" },

  // Team Card / Content Health Card
  sideCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 6px rgba(0,0,0,0.02)",
    padding: "20px",
    display: "flex",
    flexDirection: "column",
  },
  sideTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: "#111827",
    marginBottom: "16px",
  },
  teamListContainer: {
    flex: 1,
    overflowY: "auto",
    marginBottom: "16px",
    paddingRight: "4px",
    maxHeight: "300px",
  },
  teamList: { display: "flex", flexDirection: "column", gap: "14px" },
  teamRow: { display: "flex", gap: "12px", alignItems: "center" },
  teamAvatarBig: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#f3f4f6",
    color: "#374151",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 14,
    flexShrink: 0,
  },
  avatarStack: { display: "inline-flex", alignItems: "center" },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: "#111827",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 11,
    border: "2px solid #fff",
    marginLeft: -6,
    overflow: "hidden", // Added overflow hidden for clipped images in stack
  },
  avatarMore: {
    marginLeft: 8,
    fontSize: 12,
    color: "#374151",
    fontWeight: 700,
  },
  teamName: {
    fontWeight: 700,
    fontSize: 13,
    color: "#1f2937",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  teamRole: { fontSize: 12, color: "#6b7280" },
  settingsButton: {
    width: "100%",
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    borderRadius: 8,
    padding: "10px",
    cursor: "pointer",
    fontWeight: 600,
    color: "#374151",
    fontSize: 13,
  },
};
